<template>
  <div data-view="google-manager">
    <router-view v-slot="slotProps">
      <component :is="slotProps?.Component" @saved="onSaved" />
    </router-view>

    <layout-logged-frame title="Promotions" has-before>
      <template v-slot:header-actions>
        <Can
          action="view"
          subject="ad-manager.order"
        >
          <v-btn
            color="primary"
            @click="$router.push({ name: 'promotions-new', query: $route.query })"
          >
            <v-icon>add</v-icon>
            {{ $t('ad_manager_add_new_title') }}
          </v-btn>
        </Can>

        <Can
          action="view-business-contact"
          subject="ad-manager.order"
        >
          <v-btn
            id="new-promotion"
            color="primary"
            :disabled="!hasEnabledForPromotionCountries"
            @click="$router.push({ name: 'promotions-new', query: { ...$route.query, flow: 'new-promotion' } })"
          >
            <v-icon>add</v-icon>
            {{ $t('new_promotion') }}
          </v-btn>
        </Can>
      </template>

      <template v-slot:before>
        <NavigationTabs :tabs="navigationTabs"/>
      </template>

      <template v-slot:content>
        <PromotionsAlerts
          v-if="isBusiness"
          :incomplete="incompleteBilling"
          :pending="pendingBilling"
          :denied="deniedBilling"
        />

        <!-- only markets we run Ad Manager for; if the list is empty the platform select falls back to user/countries -->
        <PromotionsFilters
          ref="ordersfilter"
          :is-skeleton-loading="isSkeletonLoading"
          :countries="adManagerCountries"
          @filter="onFilter"
          @clearFilters="onClearFilters"
        />

        <table-base
          :columns="isBusiness ? columnsBC : columns"
          :items="orders"
          :pagination="pagination"
          :pages="totalPages"
          :is-loading="isLoading"
          :can-edit-columns="false"
          :on-select-item="onSelectItem"
          :on-change-page="onChangePage"
          :on-update-pagination="onUpdatePagination"
          :sync-to-url="true"
          @init-pagination="onInitPagination"
          :no-data-title="$t('no_promotions')"
          :no-data-subtitle="$t('start_to_use_our_products')"
          has-custom-items-template
        >
          <template v-slot:item-cell="scope">
            <template v-if="scope.cell.column.value === 'isArchived'">
              {{ displayArchivedColumn(scope.cell.item) }}
            </template>

            <template v-else-if="scope.cell.column.value === 'contract_deal.value'">
              {{ displayContractColumn(scope.cell.item) }}
            </template>

            <template v-else-if="scope.cell.column.value === 'lines'">
              <LineItemStatus
                :items="scope.cell.item"
                :item-name="scope.cell.item.name"
                :expanded="expandedRows"
              />
            </template>

            <template v-else-if="scope.cell.column.value === 'impressions'">
              <LineItemImpressions
                :items="scope.cell.item"
                :item-name="scope.cell.item.name"
                :expanded="expandedRows"
                :on-toggle="onToggleRow"
              />
            </template>

            <template v-else>
              {{ displayColumnValue(scope.cell.item, scope.cell.column.value) }}
            </template>
          </template>
        </table-base>
      </template>
    </layout-logged-frame>
  </div>
</template>

<script>
import PromotionsFilters from '../../components/forms/filters/Promotions';
import urlPaginationRestore from '@/mixins/urlPaginationRestore';
import LineItemStatus from '../../components/panels/lineItems/Status';
import LineItemImpressions from '../../components/panels/lineItems/Impressions';
import PromotionsAlerts from '../../components/alerts/Promotions';
import columns from '../../config/promotions/orderColumns';
import columnsBC from '../../config/promotions/orderColumnsBC';
import navigationTabs from '../../config/promotions/navigationTabs';
import NavigationTabs from '../../components/NavigationTabs'
import { extractNestedProp, isStr } from '@/utils';

export default {
  mixins: [urlPaginationRestore],
    components: {
      PromotionsFilters,
      LineItemStatus,
      LineItemImpressions,
      PromotionsAlerts,
      NavigationTabs
    },
    data() {
      return {
        navigationTabs,
        columns,
        columnsBC,
        totalPages: 0,
        pagination: {
          order: 'desc' ,
          page: 1,
          rowsPerPage: 15,
          sortBy: '',
          totalItems: 0,
          search: ''
        },
        query: { page: 1, archived: 0, per_page: 15 },
        expandedRows: [],
        isSkeletonLoading: true,
        isLoading: false,
      };
    },
    computed: {
      disabledForPromotionCountries() {
        return this.$store.getters['businesses/activeItem']?.disabledForPromotionCountries ?? [];
      },
      hasEnabledForPromotionCountries() {
        return this.disabledForPromotionCountries.length !== this.enabledPromoCountries.length;
      },
      activeUser() {
        return this.$store.getters['auth/user'];
      },
      enabledPromoCountries() {
        return this.$store.getters['businessUsers/enabledPromoCountries'];
      },
      userCountries() {
        return this.$store.getters['users/countries'];
      },
      adManagerCountries() {
        // An internal user manages markets we do not run Ad Manager for (SE, US). Those have no
        // trafficker id, so filtering the promotions by them cannot return anything and the API
        // rejects it - do not offer them. A business user's enabled list is GAM-scoped already.
        if (this.enabledPromoCountries.length > 0) {
          return this.enabledPromoCountries;
        }

        return this.userCountries.filter((country) => country.ad_manager);
      },
      orders() {
        return this.$store.getters['promotions/items'];
      },
      billingCountriesByStatus() {
        return this.$store.getters['businesses/activeItem']?.billingCountriesByStatus ?? [];
      },
      totalCount() {
        return this.pagination.totalItems || 0;
      },
      isBusiness() {
        return this.$ability.can('view-business-contact', 'ad-manager.order');
      },
      incompleteBilling() {
        return this.alertMessageBaseCondition('no_billing');
      },
      pendingBilling() {
        return this.alertMessageBaseCondition('networks_in_review');
      },
      deniedBilling() {
        return this.alertMessageBaseCondition('networks_denied');
      },
    },
    created() {
      const { isBusiness, getBillingCountriesByStatus, getEnabledCountriesForPromotion } = this;

      if (isBusiness) {
        getBillingCountriesByStatus();
        getEnabledCountriesForPromotion();
      }

      // Wrapped like the other two triggers: this view fetches in its own
      // created() as well, so a deep link had three initial fetches racing and
      // the unfiltered page 1 went out first.
      this.runInitialFetch(() => this.getOrders(this.query).catch((err) => err));
    },
    methods: {
      // Emitted by the dialog routes after a successful save. Keeps the
      // refetch in this view, with this view's own query, and out of the URL.
      onSaved() {
        this.getOrders(this.query);
      },
      getEnabledCountriesForPromotion() {
        return this.$store.dispatch('businessUsers/getEnabledCountriesForPromotion');
      },
      getOrders(query = {}) {
        this.isLoading = true;

        return this.$store.dispatch('promotions/getOrders', query)
          .then(res => {
            const { itemsMeta } = this.$store.getters['promotions/itemsData'];
            const { total, current_page, last_page } = itemsMeta;

            this.pagination.page = current_page;
            this.pagination.totalItems = total;
            this.totalPages = last_page;
            this.isSkeletonLoading = false;
            this.isLoading = false;

            return res;
          })
          .catch((err) => {
            this.isSkeletonLoading = false;
            this.isLoading = false;

            return err;
          });
      },
      getBillingCountriesByStatus() {
        return this.$store.dispatch('businesses/getBillingCountriesByStatus');
      },
      getFilteredOrders() {
        if (this.$refs.ordersfilter) {
          this.$refs.ordersfilter.onFilter();
        }
      },
      onFilter(filters) {
        this.query = {
          ...this.query,
          search: filters.search,
          date_filter: filters.date_filter,
          advertiser_id: filters.advertiser_id ?? null,
          created_by: filters.created_by ?? null,
          order_type: filters.order_type ?? -1,
          countries: filters.countries,
          page: 1,
        };

        this.runInitialFetch(() => this.getOrders(this.query));
      },
      onClearFilters() {
        const { sortBy } = this.pagination;
        this.query = { page: 1, sort: sortBy, archived: 0, per_page: 15 };

        this.getOrders(this.query);
      },
      onSelectItem(order) {
        this.$router.push({ name: 'promotion-details', params: { id: order.id }, query: this.$route.query });
      },
      displayColumnValue(item, columnValue) {
        return extractNestedProp(item, columnValue);
      },
      displayArchivedColumn(item) {
        return item.isArchived ? 'Yes' : 'No';
      },
      displayContractColumn(item) {
        if (item?.contract_deal?.value.length) {
          return item?.contract_deal?.value;
        }
        return '-';
      },
      onChangePage(page) {
        const { query, pagination } = this;
        const { order, sortBy } = pagination;
        const nextQuery = {
          ...query,
          page,
          sort: sortBy,
          order,
        };
        this.query = nextQuery;

        this.getOrders(nextQuery);
      },
      onUpdatePagination({ page, order, sortBy }) {
        if (!sortBy) return null;

        const { query, pagination } = this;
        this.pagination.order = order;
        this.pagination.sortBy = isStr(sortBy) ? sortBy : pagination.sortBy;

        const nextQuery = {
          ...query,
          page,
          sort: this.pagination.sortBy,
          order,
        };
        this.query = nextQuery;

        this.runInitialFetch(() => this.getOrders(nextQuery));
      },
      onToggleRow(name) {
        const itemIndex = this.expandedRows.findIndex(i => i === name);

        if (itemIndex > -1) {
          this.expandedRows.splice(itemIndex, 1);
        } else {
          this.expandedRows.push(name);
        }
      },
      alertMessageBaseCondition(arrayName) {
        const { billingCountriesByStatus } = this;

        // eslint-disable-next-line max-len
        if ( billingCountriesByStatus && billingCountriesByStatus[arrayName] && billingCountriesByStatus[arrayName].length) {
          return billingCountriesByStatus[arrayName];
        }

        return [];
      },
    }
  };
</script>

<style lang="scss">

</style>
